
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { InventoryTransferExcludedItems } from '@/collections/inventory_transfer_excluded_items';
import { InventoryTransferSuggestionItems, RouteSummaryInterface } from '@/collections/inventory_transfer_suggestion_items';
import type { ProductFilterCondition } from '@/collections/inventory_transfer_suggestion_items';
import { FilterColumnList } from '@/collections/filter_columns';
import { ExcludedItemInterface, InventoryTransferSuggestionItemInterface } from '@/models/inventory_transfer_suggestion';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { InventoryTransferRouteList } from '@/collections/inventory_transfer_routes';
import { InventoryTransferRouteModel } from '@/models/inventory_transfer_route';
import { InventoryTransfers } from '@/collections/inventory_transfers';
import { CreateTransferRequest } from '@/models/inventory_transfer';
import { EventBus } from '@/main';
import { StoreModel } from '@/models/store';
import { isEmbedded, redirectExternal } from '@/utils/shopifyBridge';
import PriorityScoreCard from '@/components/PriorityScoreCard.vue';
import PriorityBadge from '@/components/PriorityBadge.vue';
import FilterColumn from '@/components/FilterColumn.vue';
import InventoryTransferPermissionDialog from '@/components/InventoryTransferPermissionDialog.vue';
import { EventHandler } from '@/modules/events';
import { CSVExportUtil } from '@/utils/csv-export';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import { ATLAS_FEATURE_LINKS, getFeatureVersion, setFeatureVersion } from '@/config/atlasFeatures';

@Component({
  components: {
    PriorityScoreCard,
    PriorityBadge,
    InventoryTransferPermissionDialog,
    CopyLinkButton,
    FilterColumn,
    TryNewAtlasBanner,
  },
})
export default class InventoryTransferSuggestionsPage extends Vue {
  @Prop({ default: null }) public app!: any;
  @Prop({ default: () => new StoreModel() }) public store!: StoreModel;
  // Data properties
  public locations: StoreLocationList = new StoreLocationList();
  public routes: InventoryTransferRouteList = new InventoryTransferRouteList();
  public transfersCollection: InventoryTransfers = new InventoryTransfers();
  private _eventHandler: EventHandler | null = null;

  // Lazy initialization of EventHandler
  private get eventHandler(): EventHandler {
    if (!this._eventHandler) {
      this._eventHandler = new EventHandler({ store: this.store });
    }
    return this._eventHandler;
  }

  public loading: boolean = false;
  public reloadingAfterTransfer: boolean = false;
  public error: string | null = null;
  public selectionMode: 'source' | 'destination' = 'destination';
  public selectedLocationId: number | null = null;
  public showExplanationDialog: boolean = false;
  public selectedItem: any = null;
  public selectedRoute: any = null;
  public selectedItems: { [key: string]: any } = {};
  public expandedPriority: boolean = false;
  public priorityFilters = {
    priorityLevels: ['Critical', 'High', 'Normal', 'Low'],
  };
  public minPrice: number | null = null;
  public maxPrice: number | null = null;

  // Product-info conditions (include/exclude via operators) applied SERVER-SIDE
  // in the OLAP query — unlike priority/vendor/price, which filter already
  // fetched rows in the backend's JS layer.
  public productFilterColumns: FilterColumnList = new FilterColumnList();

  // Columns offered by the Product Filters condition builder. Codes must match
  // the transfer OLAP cube's attribute codes (vendor is 'vendor' here — NOT the
  // buying list's 'Vendor Name'); luisMapping drives the value autocomplete via
  // GET /dimensions/{luisMapping}.
  public productFilterReportColumns = [
    { name: 'Product', code: 'Item[Item Description]', dataType: 'text', luisMapping: 'by_product' },
    { name: 'Variant', code: 'variant', dataType: 'text', luisMapping: 'variant' },
    { name: 'SKU', code: 'product_sku', dataType: 'text', luisMapping: 'by_product_sku' },
    { name: 'Vendor', code: 'vendor', dataType: 'text', luisMapping: 'by_vendor' },
    { name: 'Collections', code: 'product_collections', dataType: 'text', luisMapping: 'product_collections' },
    { name: 'Product Type', code: 'product_type', dataType: 'text', luisMapping: 'by_product_type' },
    { name: 'Product Tags', code: 'product_tags', dataType: 'text', luisMapping: 'by_product_tag' },
  ];

  public filterPanelExpanded: number = 0; // Keep filter panel expanded by default
  public showPermissionDialog: boolean = false;
  public showTransferConfirmDialog: boolean = false;
  public confirmTransferData: any = null;
  // Client-side pagination for the Confirm Transfer dialog item list (Story 14 Task 5).
  // select-all can feed thousands of items here, so only one page is rendered at a time.
  public confirmDialogPage: number = 1;
  public confirmDialogItemsPerPage: number = 50;
  public lastCreatedTransfer: any = null;
  public showTransferSuccessDialog: boolean = false;

  // Table headers for suggestions
  public suggestionHeaders = [
    { text: '', value: 'selection', sortable: false, width: '50px' },
    { text: 'Product Information', value: 'productInfo', sortable: false, width: '60%' },
    { text: 'Suggested Quantity', value: 'suggestedQuantity', sortable: false, width: '40%', align: 'center' },
  ];

  // Default page size for each per-route suggestions table (Story 14/15)
  public suggestionItemsPerPage: number = 50;

  // Story 15: per-route server-side pagination state, keyed `${fromId}_${toId}`.
  // summary arrives with the route's first fetch (need_count=true) and replaces
  // the old /suggestions?summary_only=true page-load call.
  public suggestionItemsState: Record<string, {
    items: InventoryTransferSuggestionItemInterface[];
    total: number;
    loading: boolean;
    loaded: boolean;
    page: number;
    itemsPerPage: number;
    summary: RouteSummaryInterface | null;
    summaryLoaded: boolean;
  }> = {};

  // Story 15: which route panels are open. All panels open on load and their
  // first pages fetch in parallel (one OLAP-backed request per route).
  public openSuggestionPanels: number[] = [];

  // Debounce for price-filter refetches (text fields fire per keystroke)
  private filterRefetchTimer: number | null = null;

  // Free-text search across suggestion items (Story 14/15). The bound query is
  // applied into appliedSuggestionSearch (and the API called) only on Enter or
  // the Search button — never while typing.
  public suggestionSearchQuery: string = '';
  public appliedSuggestionSearch: string = '';

  // Table headers for excluded items (Story 13)
  public excludedItemHeaders = [
    { text: 'Product', value: 'productTitle', width: '30%' },
    { text: 'SKU', value: 'sku', width: '15%' },
    { text: 'Reason', value: 'exclusionReason', width: '25%' },
    { text: 'Details', value: 'reasonDetail', width: '30%' },
  ];

  // Active exclusion reason filters (empty = show all). Triggers refetch of expanded panels.
  public selectedExclusionReasons: string[] = [];

  // Free-text search for excluded items. Triggers refetch (debounced).
  public excludedSearchQuery: string = '';
  private excludedSearchDebounceTimer: number | null = null;

  // Per-route state for the server-side excluded items table.
  // Key: `${fromLocationId}_${toLocationId}`
  public excludedItemsState: {
    [routeKey: string]: {
      items: ExcludedItemInterface[];
      total: number;
      loading: boolean;
      page: number;
      itemsPerPage: number;
      loaded: boolean;
    };
  } = {};

  // Tracks which expansion panels are open. Vuetify v-expansion-panels in multi mode wants an array.
  public openExclusionPanels: number[] = [];

  get showTryNewTransfersBanner(): boolean {
    return getFeatureVersion('inventoryTransfers') === 'classic';
  }

  public switchTransfersToAtlas(): void {
    setFeatureVersion('inventoryTransfers', 'atlas');
    this.$router.push(
      ATLAS_FEATURE_LINKS.inventoryTransfers['/inventory-transfers/suggestions'],
    );
  }


  // Watchers for NaN normalization
  @Watch('minPrice')
  public onMinPriceChange(value: number | null): void {
    // Normalize NaN and invalid numbers to null
    if (value !== null && !Number.isFinite(value)) {
      this.$nextTick(() => {
        this.minPrice = null;
      });
    }
  }

  @Watch('maxPrice')
  public onMaxPriceChange(value: number | null): void {
    // Normalize NaN and invalid numbers to null
    if (value !== null && !Number.isFinite(value)) {
      this.$nextTick(() => {
        this.maxPrice = null;
      });
    }
  }

  // Computed properties
  public get locationMap(): Map<number, string> {
    const map = new Map<number, string>();
    this.locations.items.forEach((location: StoreLocationModel) => {
      const l = location as any;
      map.set(l.id, l.name);
    });
    return map;
  }

  public get availableLocations() {
    if (!this.routes.items || this.routes.items.length === 0) {
      return this.locations.items;
    }

    const availableLocationIds = new Set<number>();

    this.routes.items.forEach((route) => {
      const r = route as InventoryTransferRouteModel;
      if (this.selectionMode === 'destination') {
        // Show locations that can be destinations
        if (r.destinationLocationId) {
          availableLocationIds.add(r.destinationLocationId);
        }
      } else {
        // Show locations that can be sources
        if (r.sourceLocationId) {
          availableLocationIds.add(r.sourceLocationId);
        }
      }
    });

    return this.locations.items.filter((location: StoreLocationModel) => {
      const l = location as any;
      return availableLocationIds.has(l.id);
    });
  }

  public get filteredRoutes(): InventoryTransferRouteModel[] {
    if (!this.selectedLocationId) { return []; }
    const locationId = this.selectedLocationId;
    return this.routes.items.filter((route) => {
      const r = route as InventoryTransferRouteModel;
      if (this.selectionMode === 'destination') {
        return r.destinationLocationId === locationId;
      } else {
        return r.sourceLocationId === locationId;
      }
    }) as InventoryTransferRouteModel[];
  }

  public get productFilterCount(): number {
    return this.productFilterColumns.items.length;
  }

  // The applied product conditions serialized for the product_filters API
  // param; undefined when none, so no param is sent.
  public get productFiltersParam(): ProductFilterCondition[] | undefined {
    if (this.productFilterColumns.items.length === 0) { return undefined; }
    return this.productFilterColumns.items.map((filter: any) => ({
      name: filter.name,
      code: filter.code,
      value: Array.isArray(filter.value) ? filter.value : [],
      operator: filter.operator || 'eq',
      dataType: filter.dataType || 'text',
    }));
  }

  public get totalActiveFilterCount(): number {
    let count = 0;
    // Count priority filter as active if not all levels are selected
    if (this.hasPriorityFilter) { count++; }
    // Count price filter (min or max)
    if (Number.isFinite(this.minPrice) || Number.isFinite(this.maxPrice)) { count++; }
    // Each product filter condition counts individually
    count += this.productFilterCount;
    return count;
  }

  public get hasAnyActiveFilter(): boolean {
    return this.hasPriorityFilter ||
           Number.isFinite(this.minPrice) || Number.isFinite(this.maxPrice) ||
           this.productFilterCount > 0;
  }

  public get hasPriorityFilter(): boolean {
    // Priority filter is active if not all levels are selected
    return this.priorityFilters.priorityLevels.length < 4;
  }

  public get priorityFilterSummary(): string {
    const levels = this.priorityFilters.priorityLevels;
    if (levels.length === 0) { return 'None'; }
    // Show all selected levels joined by comma
    return levels.join(', ');
  }

  public get priorityLevelOptions() {
    return [
      { value: 'Critical', label: 'Critical', color: '#f6687b', icon: 'mdi-alert-octagon' },
      { value: 'High', label: 'High', color: '#faaf3b', icon: 'mdi-alert-triangle' },
      { value: 'Normal', label: 'Normal', color: '#59C8E4', icon: 'mdi-information' },
      { value: 'Low', label: 'Low', color: '#99be50', icon: 'mdi-check-circle' },
    ];
  }

  public get priceRangeText(): string {
    const hasMinPrice = Number.isFinite(this.minPrice);
    const hasMaxPrice = Number.isFinite(this.maxPrice);

    if (hasMinPrice && hasMaxPrice) {
      return `$${this.minPrice} - $${this.maxPrice}`;
    } else if (hasMinPrice) {
      return `≥ $${this.minPrice}`;
    } else if (hasMaxPrice) {
      return `≤ $${this.maxPrice}`;
    }
    return '—'; // Em dash for empty/invalid values
  }

  // Story 15: route view-models built from the CONFIGURED routes (cheap Postgres
  // config, loaded at page start) merged with each route's fetched summary
  // (need_count on its first /suggestion-items call). No upfront summary
  // endpoint call needed — the page structure renders immediately.
  public get filteredSuggestions() {
    return this.filteredRoutes.map((r: any) => {
      const fromLocationId = Number(r.sourceLocationId);
      const toLocationId = Number(r.destinationLocationId);
      const state = this.suggestionItemsState[`${fromLocationId}_${toLocationId}`];
      return {
        fromLocationId,
        toLocationId,
        fromLocationName: this.getLocationName(fromLocationId),
        toLocationName: this.getLocationName(toLocationId),
        summary: state?.summary
          ? {
            countVariants: state.summary.countVariants,
            totalSuggestedQuantity: state.summary.totalSuggestedQuantity,
            totalPendingQuantity: state.summary.totalPendingQuantity,
          }
          : null,
        noSuggestionSummary: state?.summary?.noSuggestionSummary || null,
        summaryLoaded: !!state?.summaryLoaded,
      };
    });
  }

  // --- Exclusion reason computed properties (Story 13) ---

  public get hasSuggestionItems(): boolean {
    // Optimistic while summaries are loading: only declare "no suggestions"
    // (which swaps in the explanation card) once every route has reported zero.
    return this.filteredSuggestions.some((s: any) =>
      !s.summaryLoaded || (s.summary?.countVariants || 0) > 0,
    );
  }

  public get hasExcludedItems(): boolean {
    // Show the explanation card whenever the user has selected a location with routes
    // and at least one route has either excluded items OR is missing from the OLAP result
    // (so we can explain that case too).
    if (this.filteredRoutes.length === 0) { return false; }
    if (this.hasSuggestionItems) { return false; }
    return true;
  }

  // Build explanation panels from ALL configured routes for the selected location.
  // Routes that have OLAP data show the breakdown table; routes without data show a
  // "no source inventory data" placeholder so merchants don't think we forgot them.
  public get filteredExclusionRoutes() {
    return this.filteredRoutes.map((r: any) => {
      const route = r as any;
      const fromId = Number(route.sourceLocationId);
      const toId = Number(route.destinationLocationId);
      // Find the matching suggestion data (if any) by route IDs
      const data = this.filteredSuggestions.find((s: any) =>
        Number(s.fromLocationId) === fromId && Number(s.toLocationId) === toId,
      ) as any;
      // "Has data" now means: the route's summary loaded AND OLAP actually
      // returned rows for it (suggestions or exclusions)
      const hasData = !!data?.summaryLoaded &&
        ((data.summary?.countVariants || 0) > 0 ||
         (data.noSuggestionSummary?.totalExcluded || 0) > 0);
      return {
        fromLocationId: fromId,
        toLocationId: toId,
        hasData,
        noSuggestionSummary: data?.noSuggestionSummary || {
          totalExcluded: 0,
          reasons: {},
          excludedItemsReturned: 0,
          excludedItemsTruncated: false,
        },
      };
    });
  }

  public get totalExcludedCount(): number {
    return this.filteredSuggestions.reduce((sum: number, s: any) => {
      return sum + (s.noSuggestionSummary?.totalExcluded || 0);
    }, 0);
  }

  public get exclusionSummary(): Record<string, { count: number; label: string }> {
    const summary: Record<string, { count: number; label: string }> = {};
    this.filteredSuggestions.forEach((s: any) => {
      if (s.noSuggestionSummary && s.noSuggestionSummary.reasons) {
        Object.entries(s.noSuggestionSummary.reasons).forEach(([reason, count]: [string, any]) => {
          if (!summary[reason]) {
            summary[reason] = { count: 0, label: this.getExclusionReasonLabel(reason) };
          }
          summary[reason].count += count;
        });
      }
    });
    return summary;
  }

  // Story 15: the route panels for the selected location. Item-level filtering
  // (search / priority / vendor / price) happens server-side per route. Panels
  // stay visible while their summary loads; routes confirmed to have zero
  // suggestions are dropped (the exclusion explanation covers them).
  public get routePanels() {
    return this.filteredSuggestions.filter(
      (suggestion) => !suggestion.summaryLoaded || (suggestion.summary?.countVariants || 0) > 0,
    );
  }

  // Story 14: true when an item matches the (already trimmed + lowercased) search term
  // against product title, variant title, SKU, barcode, or vendor.
  private itemMatchesSearch(item: any, search: string): boolean {
    const fields = [item.productTitle, item.variantTitle, item.sku, item.barcode, item.vendor];
    return fields.some((field) => field != null && String(field).toLowerCase().includes(search));
  }

  // Story 15: client-side replica of the server's item filters, used to tell
  // whether an already-selected item (whose full data we hold) still matches the
  // active filters — powers the "hidden by filters" hint without a server call.
  private itemMatchesActiveFilters(item: any): boolean {
    const search = (this.appliedSuggestionSearch || '').trim().toLowerCase();
    if (search && !this.itemMatchesSearch(item, search)) {
      return false;
    }
    const priorityLevel = this.getPriorityLevel(item.priorityScore || 0);
    if (!this.priorityFilters.priorityLevels.includes(priorityLevel)) {
      return false;
    }
    const priceFiltersActive = Number.isFinite(this.minPrice) || Number.isFinite(this.maxPrice);
    if (priceFiltersActive) {
      const hasValidPrice = typeof item.unitPrice === 'number' && Number.isFinite(item.unitPrice);
      if (!hasValidPrice) { return false; }
      if (Number.isFinite(this.minPrice) && item.unitPrice < this.minPrice!) { return false; }
      if (Number.isFinite(this.maxPrice) && item.unitPrice > this.maxPrice!) { return false; }
    }
    return true;
  }

  public get totalQuantity(): number {
    if (!this.confirmTransferData || !this.confirmTransferData.items) { return 0; }
    return this.confirmTransferData.items.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0);
  }

  // Mirrors the backend's MAX_TRANSFER_ITEMS cap so oversized selections are
  // blocked in the dialog instead of failing on submit.
  public readonly maxTransferItems: number = 1000;

  public get isTransferOverItemCap(): boolean {
    return !!this.confirmTransferData
      && (this.confirmTransferData.items?.length || 0) > this.maxTransferItems;
  }

  public get isTransferValid(): boolean {
    if (!this.confirmTransferData || !this.confirmTransferData.items || this.confirmTransferData.items.length === 0) {
      return false;
    }

    if (this.isTransferOverItemCap) {
      return false;
    }

    return this.confirmTransferData.items.every((item: any) =>
      item.quantity &&
      item.quantity > 0 &&
      item.quantity <= item.sourceInventory,
    );
  }

  // Story 14 Task 5: number of pages in the Confirm Transfer dialog item list.
  public get confirmDialogPageCount(): number {
    if (!this.confirmTransferData || !this.confirmTransferData.items) { return 0; }
    return Math.ceil(this.confirmTransferData.items.length / this.confirmDialogItemsPerPage);
  }

  // Story 14 Task 5: the current page's items, each paired with its ABSOLUTE index in
  // confirmTransferData.items so quantity edits / removals target the correct line.
  public get pagedConfirmItems(): Array<{ item: any; index: number }> {
    if (!this.confirmTransferData || !this.confirmTransferData.items) { return []; }
    const start = (this.confirmDialogPage - 1) * this.confirmDialogItemsPerPage;
    const end = start + this.confirmDialogItemsPerPage;
    return this.confirmTransferData.items
      .slice(start, end)
      .map((item: any, i: number) => ({ item, index: start + i }));
  }

  // Methods

  // Priority methods
  public getPriorityLevel(score: number): string {
    if (score >= 80) { return 'Critical'; }
    if (score >= 60) { return 'High'; }
    if (score >= 30) { return 'Normal'; }
    return 'Low';
  }

  public getPriorityLevelClass(score: number): string {
    const level = this.getPriorityLevel(score);
    return `priority-${level.toLowerCase()}`;
  }

  public getPriorityColor(score: number): string {
    const level = this.getPriorityLevel(score);
    switch (level.toLowerCase()) {
      case 'critical': return '#f6687b';
      case 'high': return '#faaf3b';
      case 'normal': return '#59C8E4';
      case 'low': return '#99be50';
      default: return '#59C8E4';
    }
  }

  public getPriorityDescription(score: number): string {
    const level = this.getPriorityLevel(score);
    switch (level.toLowerCase()) {
      case 'critical':
        return 'Immediate action required. High risk of stockout with significant revenue impact.';
      case 'high':
        return 'Urgent attention needed. Elevated risk levels require prompt transfer processing.';
      case 'normal':
        return 'Standard priority. Transfer should be processed in normal workflow.';
      case 'low':
        return 'Low urgency. Can be scheduled with other routine transfers.';
      default:
        return 'Standard priority transfer.';
    }
  }

  public getPriorityIcon(score: number): string {
    const level = this.getPriorityLevel(score);
    switch (level.toLowerCase()) {
      case 'critical': return 'mdi-alert-octagon';
      case 'high': return 'mdi-alert-triangle';
      case 'normal': return 'mdi-information';
      case 'low': return 'mdi-check-circle';
      default: return 'mdi-information';
    }
  }

  // --- Exclusion reason helpers (Story 13) ---

  public getExclusionReasonColor(reason: string): string {
    switch (reason) {
      case 'DESTINATION_WELL_STOCKED': return '#99be50';
      case 'NO_DESTINATION_SALES': return '#FF9800';
      case 'COVERED_BY_PENDING_TRANSFERS': return '#7B1FA2';
      case 'SOURCE_EMPTY': return '#c62828';
      case 'SOURCE_BELOW_OWN_TARGET': return '#f6687b';
      case 'SOURCE_ALLOCATED_TO_OTHER_ROUTES': return '#1976D2';
      default: return 'grey';
    }
  }

  // Light background + dark text pairs that match the existing app chip pattern
  // (e.g. priority/vendor/price filter chips at the top of the suggestions card).
  public getReasonChipBg(reason: string): string {
    switch (reason) {
      case 'DESTINATION_WELL_STOCKED': return '#E8F5E9';
      case 'NO_DESTINATION_SALES': return '#F3E5F5';
      case 'COVERED_BY_PENDING_TRANSFERS': return '#E8EAF6';
      case 'SOURCE_EMPTY': return '#FFEBEE';
      case 'SOURCE_BELOW_OWN_TARGET': return '#FFF3E0';
      case 'SOURCE_ALLOCATED_TO_OTHER_ROUTES': return '#E3F2FD';
      default: return 'grey lighten-3';
    }
  }

  public getReasonChipText(reason: string): string {
    switch (reason) {
      case 'DESTINATION_WELL_STOCKED': return '#2E7D32';
      case 'NO_DESTINATION_SALES': return '#7B1FA2';
      case 'COVERED_BY_PENDING_TRANSFERS': return '#3949AB';
      case 'SOURCE_EMPTY': return '#C62828';
      case 'SOURCE_BELOW_OWN_TARGET': return '#E65100';
      case 'SOURCE_ALLOCATED_TO_OTHER_ROUTES': return '#1976D2';
      default: return 'grey darken-1';
    }
  }

  public getExclusionReasonIcon(reason: string): string {
    switch (reason) {
      case 'DESTINATION_WELL_STOCKED': return 'mdi-check-circle';
      case 'NO_DESTINATION_SALES': return 'mdi-chart-line-variant';
      case 'COVERED_BY_PENDING_TRANSFERS': return 'mdi-truck-check';
      case 'SOURCE_EMPTY': return 'mdi-package-variant-closed-remove';
      case 'SOURCE_BELOW_OWN_TARGET': return 'mdi-warehouse';
      case 'SOURCE_ALLOCATED_TO_OTHER_ROUTES': return 'mdi-priority-high';
      default: return 'mdi-help-circle';
    }
  }

  public getExclusionReasonLabel(reason: string): string {
    switch (reason) {
      case 'DESTINATION_WELL_STOCKED': return 'Destination well-stocked';
      case 'NO_DESTINATION_SALES': return 'No sales at destination';
      case 'COVERED_BY_PENDING_TRANSFERS': return 'Covered by pending transfers';
      case 'SOURCE_EMPTY': return 'Source out of stock';
      case 'SOURCE_BELOW_OWN_TARGET': return 'Source below its own target';
      case 'SOURCE_ALLOCATED_TO_OTHER_ROUTES': return 'Source allocated to other routes';
      default: return 'Other';
    }
  }

  public isExclusionReasonSelected(reason: string): boolean {
    return this.selectedExclusionReasons.includes(reason);
  }

  public toggleExclusionReasonFilter(reason: string): void {
    const idx = this.selectedExclusionReasons.indexOf(reason);
    if (idx >= 0) {
      this.selectedExclusionReasons.splice(idx, 1);
    } else {
      this.selectedExclusionReasons.push(reason);
    }
    this.refetchAllOpenExcludedPanels();
  }

  public clearExclusionReasonFilter(): void {
    this.selectedExclusionReasons = [];
    this.refetchAllOpenExcludedPanels();
  }

  public clearExcludedSearch(): void {
    this.excludedSearchQuery = '';
    this.refetchAllOpenExcludedPanels();
  }

  // --- Server-side excluded items fetching ---

  private getRouteStateKey(route: any): string {
    return `${route.fromLocationId}_${route.toLocationId}`;
  }

  public getExcludedState(route: any) {
    const key = this.getRouteStateKey(route);
    if (!this.excludedItemsState[key]) {
      // Vue 2 reactivity: use Vue.set to add new keys reactively
      this.$set(this.excludedItemsState, key, {
        items: [],
        total: 0,
        loading: false,
        page: 1,
        itemsPerPage: 20,
        loaded: false,
      });
    }
    return this.excludedItemsState[key];
  }

  public async fetchExcludedItemsForRoute(route: any): Promise<void> {
    const key = this.getRouteStateKey(route);
    const state = this.getExcludedState(route);
    state.loading = true;
    try {
      const result = await InventoryTransferExcludedItems.fetch({
        fromLocationId: route.fromLocationId,
        toLocationId: route.toLocationId,
        page: state.page,
        perPage: state.itemsPerPage,
        reason: this.selectedExclusionReasons,
        search: this.excludedSearchQuery,
        // Keep the excluded panel consistent with the product-filtered suggestions
        productFilters: this.productFiltersParam,
      });
      state.items = result.items;
      state.total = result.total;
      state.loaded = true;
    } catch (e) {
      console.error('Failed to fetch excluded items:', e);
      state.items = [];
      state.total = 0;
    } finally {
      state.loading = false;
    }
  }

  public onExcludedTableOptionsChange(route: any, options: { page: number; itemsPerPage: number }): void {
    const state = this.getExcludedState(route);
    if (state.page !== options.page || state.itemsPerPage !== options.itemsPerPage) {
      state.page = options.page;
      state.itemsPerPage = options.itemsPerPage;
      this.fetchExcludedItemsForRoute(route);
    }
  }

  public onExclusionPanelsChange(openIndices: number[] | undefined): void {
    this.openExclusionPanels = openIndices || [];
    // Lazy-load any newly opened panels (skip routes without OLAP data)
    this.openExclusionPanels.forEach((idx) => {
      const route = this.filteredExclusionRoutes[idx];
      if (route && (route as any).hasData) {
        const state = this.getExcludedState(route);
        if (!state.loaded && !state.loading) {
          this.fetchExcludedItemsForRoute(route);
        }
      }
    });
  }

  private refetchAllOpenExcludedPanels(): void {
    this.openExclusionPanels.forEach((idx) => {
      const route = this.filteredExclusionRoutes[idx];
      if (route && (route as any).hasData) {
        const state = this.getExcludedState(route);
        state.page = 1; // reset to first page when filter changes
        this.fetchExcludedItemsForRoute(route);
      }
    });
  }

  @Watch('excludedSearchQuery')
  public onExcludedSearchChange(newVal: string, oldVal: string): void {
    if (newVal === oldVal) { return; }
    if (this.excludedSearchDebounceTimer !== null) {
      window.clearTimeout(this.excludedSearchDebounceTimer);
    }
    this.excludedSearchDebounceTimer = window.setTimeout(() => {
      this.refetchAllOpenExcludedPanels();
      this.excludedSearchDebounceTimer = null;
    }, 350);
  }

  // Story 15: search is explicit — applied on Enter or the Search button, not
  // while typing. clearable can set the bound value to null, so coerce to ''.
  public applySuggestionSearch(): void {
    const query = this.suggestionSearchQuery || '';
    if (query === this.appliedSuggestionSearch) { return; }
    this.appliedSuggestionSearch = query;
    this.refetchAllOpenSuggestionPanels();
  }

  // Clearing via the field's ✕ resets results immediately (expected behavior
  // for the clear control, unlike typing).
  public clearSuggestionSearch(): void {
    this.suggestionSearchQuery = '';
    if (this.appliedSuggestionSearch !== '') {
      this.appliedSuggestionSearch = '';
      this.refetchAllOpenSuggestionPanels();
    }
  }

  // --- Story 15: server-side suggestion items fetching (per route) ---

  private getSuggestionStateKey(route: any): string {
    return `${route.fromLocationId}_${route.toLocationId}`;
  }

  public getSuggestionState(route: any) {
    const key = this.getSuggestionStateKey(route);
    if (!this.suggestionItemsState[key]) {
      // Vue 2 reactivity: use Vue.set to add new keys reactively
      this.$set(this.suggestionItemsState, key, {
        items: [],
        total: 0,
        loading: false,
        loaded: false,
        page: 1,
        itemsPerPage: this.suggestionItemsPerPage,
        summary: null,
        summaryLoaded: false,
      });
    }
    return this.suggestionItemsState[key];
  }

  // The filter params shared by every per-route items request. Priority levels
  // are only sent when the merchant has narrowed them (all 4 = no filter).
  private currentItemFilterParams() {
    const allLevels = this.priorityFilters.priorityLevels.length >= 4;
    return {
      search: this.appliedSuggestionSearch || undefined,
      priorityLevels: allLevels ? undefined : [...this.priorityFilters.priorityLevels],
      minPrice: Number.isFinite(this.minPrice) ? this.minPrice : undefined,
      maxPrice: Number.isFinite(this.maxPrice) ? this.maxPrice : undefined,
      productFilters: this.productFiltersParam,
    };
  }

  public async fetchSuggestionItemsForRoute(route: any): Promise<void> {
    const state = this.getSuggestionState(route);
    // Every priority chip deselected = nothing can match. Short-circuit instead
    // of calling the API: an empty priority_levels param would be dropped and
    // the server would return UNFILTERED results.
    if (this.priorityFilters.priorityLevels.length === 0) {
      state.items = [];
      state.total = 0;
      state.loaded = true;
      state.loading = false;
      return;
    }
    state.loading = true;
    try {
      const result = await InventoryTransferSuggestionItems.fetch({
        fromLocationId: route.fromLocationId,
        toLocationId: route.toLocationId,
        page: state.page,
        perPage: state.itemsPerPage,
        // The route summary only needs to be fetched once — page and JS-side
        // filter changes don't alter it. Product filters DO (they narrow the
        // OLAP rows the summary is tallied from), so onProductFiltersChange
        // resets summaryLoaded to force a re-tally.
        needCount: !state.summaryLoaded,
        ...this.currentItemFilterParams(),
      });
      state.items = result.items;
      state.total = result.total;
      state.loaded = true;
      if (result.routeSummary) {
        state.summary = result.routeSummary;
        state.summaryLoaded = true;
      }
    } catch (e) {
      console.error('Failed to fetch suggestion items:', e);
      state.items = [];
      state.total = 0;
    } finally {
      state.loading = false;
    }
  }

  public onSuggestionTableOptionsChange(route: any, options: { page: number; itemsPerPage: number }): void {
    const state = this.getSuggestionState(route);
    if (state.page !== options.page || state.itemsPerPage !== options.itemsPerPage) {
      state.page = options.page;
      state.itemsPerPage = options.itemsPerPage;
      this.fetchSuggestionItemsForRoute(route);
    }
  }

  public onSuggestionPanelsChange(openIndices: number[] | undefined): void {
    this.openSuggestionPanels = openIndices || [];
    // Lazy-load any newly opened panels
    this.openSuggestionPanels.forEach((idx) => {
      const route = this.routePanels[idx];
      if (route) {
        const state = this.getSuggestionState(route);
        if (!state.loaded && !state.loading) {
          this.fetchSuggestionItemsForRoute(route);
        }
      }
    });
  }

  public refetchAllOpenSuggestionPanels(): void {
    this.openSuggestionPanels.forEach((idx) => {
      const route = this.routePanels[idx];
      if (route) {
        const state = this.getSuggestionState(route);
        state.page = 1; // land on the first page of the new filter result
        this.fetchSuggestionItemsForRoute(route);
      }
    });
  }

  // Reset per-route state, open every route panel, and load each route's first
  // page (+ its summary via need_count) in parallel.
  public initSuggestionPanels(): Promise<void[]> {
    this.suggestionItemsState = {};
    this.openSuggestionPanels = this.routePanels.map((_, i) => i);
    return Promise.all(
      this.routePanels.map((route) => this.fetchSuggestionItemsForRoute(route)),
    );
  }

  // Debounced refetch for filter changes that fire rapidly (price text fields)
  private scheduleFilterRefetch(): void {
    if (this.filterRefetchTimer !== null) {
      window.clearTimeout(this.filterRefetchTimer);
    }
    this.filterRefetchTimer = window.setTimeout(() => {
      this.filterRefetchTimer = null;
      this.refetchAllOpenSuggestionPanels();
    }, 300);
  }

  @Watch('minPrice')
  public onMinPriceFilterChange(newVal: number | null, oldVal: number | null): void {
    if (newVal === oldVal) { return; }
    this.scheduleFilterRefetch();
  }

  @Watch('maxPrice')
  public onMaxPriceFilterChange(newVal: number | null, oldVal: number | null): void {
    if (newVal === oldVal) { return; }
    this.scheduleFilterRefetch();
  }

  // FilterColumn emits the applied condition list on Apply / clear / row delete.
  // Product filters are server-side, so they also change each route's summary
  // (need_count) and the excluded panel — invalidate and refetch both.
  public onProductFiltersChange(filters: FilterColumnList): void {
    const before = JSON.stringify(this.productFiltersParam || []);
    this.productFilterColumns = filters;
    const after = JSON.stringify(this.productFiltersParam || []);
    if (before === after) { return; }
    Object.values(this.suggestionItemsState).forEach((state) => {
      state.summaryLoaded = false;
      state.summary = null;
    });
    this.refetchAllOpenSuggestionPanels();
    this.refetchAllOpenExcludedPanels();
  }

  // Resets the FilterColumn UI; its clearFilter() emits input([]) which lands
  // in onProductFiltersChange for the state reset + refetch. Safe as a global
  // event: this page has the only FilterColumn instance.
  public clearProductFilters(): void {
    EventBus.$emit('clear-filter-column');
  }

  // Filter methods
  public clearPriceFilters(): void {
    this.minPrice = null;
    this.maxPrice = null;
    // The price watchers handle the refetch
  }

  public clearAllFilters(): void {
    this.resetPriorityFilters();
    this.clearPriceFilters();
    this.clearProductFilters();
  }

  public resetPriorityFilters(): void {
    this.priorityFilters.priorityLevels = ['Critical', 'High', 'Normal', 'Low'];
    this.refetchAllOpenSuggestionPanels();
  }

  public togglePriorityLevel(level: string): void {
    const index = this.priorityFilters.priorityLevels.indexOf(level);
    if (index > -1) {
      this.priorityFilters.priorityLevels.splice(index, 1);
    } else {
      this.priorityFilters.priorityLevels.push(level);
    }
    this.refetchAllOpenSuggestionPanels();
  }

  public showOnlyCritical(): void {
    this.priorityFilters.priorityLevels = ['Critical'];
    this.refetchAllOpenSuggestionPanels();
  }

  public showHighPriority(): void {
    this.priorityFilters.priorityLevels = ['Critical', 'High'];
    this.refetchAllOpenSuggestionPanels();
  }

  // Currency formatting method
  public formatCurrency(
    value: number | string | null | undefined,
    currency?: string,
    locale?: string
  ): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return 'N/A';
    }

    // Use provided currency or fall back to store currency or USD
    const currencyCode = currency || this.store?.information?.currency || 'USD';

    // Use provided locale or fall back to browser locale or undefined
    const localeCode = locale || (typeof navigator !== 'undefined' ? navigator.language : undefined);

    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  // Priority dialog methods
  public showPriorityDetails(item: any, parentSuggestion?: any): void {
    // Story 15: the route is passed explicitly from the panel context (route
    // models no longer carry item arrays to search through)
    if (parentSuggestion) {
      const route = this.routes.items.find((r) =>
        r.sourceLocationId === parentSuggestion.fromLocationId &&
        r.destinationLocationId === parentSuggestion.toLocationId,
      );

      this.selectedItem = item;
      this.selectedRoute = route;
      this.showExplanationDialog = true;
    }
  }

  // Selection methods
  public toggleItemSelection(item: any, suggestion: any): void {
    const itemKey = `${item.variantId}-${suggestion.fromLocationId}-${suggestion.toLocationId}`;

    if (this.selectedItems[itemKey]) {
      // Remove from selection
      this.$delete(this.selectedItems, itemKey);
    } else {
      // Add to selection with route context
      this.$set(this.selectedItems, itemKey, {
        ...item,
        fromLocationId: suggestion.fromLocationId,
        toLocationId: suggestion.toLocationId,
        fromLocationName: this.getLocationName(suggestion.fromLocationId),
        toLocationName: this.getLocationName(suggestion.toLocationId),
      });
    }
  }

  public isItemSelected(item: any, suggestion: any): boolean {
    const itemKey = `${item.variantId}-${suggestion.fromLocationId}-${suggestion.toLocationId}`;
    return !!this.selectedItems[itemKey];
  }

  public clearSelection(): void {
    this.selectedItems = {};
  }

  public get hasSelectedItems(): boolean {
    return Object.keys(this.selectedItems).length > 0;
  }

  public get selectedItemsCount(): number {
    return Object.keys(this.selectedItems).length;
  }

  public get hasSuggestions(): boolean {
    return this.routePanels.length > 0;
  }


  public getSelectedItemsForRoute(suggestion: any): any[] {
    const routeKey = `${suggestion.fromLocationId}-${suggestion.toLocationId}`;
    return Object.values(this.selectedItems).filter((item) => {
      const itemRouteKey = `${item.fromLocationId}-${item.toLocationId}`;
      return itemRouteKey === routeKey;
    });
  }

  // Story 15: count of a route's selected items that no longer match the active
  // search/filters. Selected items carry their full data, so the check runs
  // client-side against the same rules the server applies. These items are
  // still included when the transfer is created.
  public getHiddenSelectedCountForRoute(suggestion: any): number {
    const selected = this.getSelectedItemsForRoute(suggestion);
    if (selected.length === 0) { return 0; }
    return selected.filter((item: any) => !this.itemMatchesActiveFilters(item)).length;
  }

  public clearRouteSelection(suggestion: any): void {
    const routeKey = `${suggestion.fromLocationId}-${suggestion.toLocationId}`;
    const keysToRemove = Object.keys(this.selectedItems).filter((key) => {
      const item = this.selectedItems[key];
      const itemRouteKey = `${item.fromLocationId}-${item.toLocationId}`;
      return itemRouteKey === routeKey;
    });

    keysToRemove.forEach((key) => {
      this.$delete(this.selectedItems, key);
    });
  }

  public async createTransferForRoute(suggestion: any): Promise<void> {
    // Check if the store has write_inventory_transfers permission
    if (this.store && this.store.needsInventoryTransferScope()) {
      // Show the permission dialog instead of creating the transfer
      this.showPermissionDialog = true;

      // Track permission dialog shown
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Permission Dialog Shown', {
          action: 'create_transfer',
          from_location_id: suggestion.fromLocationId,
          to_location_id: suggestion.toLocationId,
        });
      }
      return;
    }

    const selectedRouteItems = this.getSelectedItemsForRoute(suggestion);
    if (selectedRouteItems.length === 0) {
      // Track empty selection attempt
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Create Attempted With No Selection', {
          from_location_id: suggestion.fromLocationId,
          to_location_id: suggestion.toLocationId,
        });
      }
      return;
    }

    // Track transfer creation initiated
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Creation Initiated', {
        from_location_id: suggestion.fromLocationId,
        from_location_name: this.getLocationName(suggestion.fromLocationId),
        to_location_id: suggestion.toLocationId,
        to_location_name: this.getLocationName(suggestion.toLocationId),
        selected_items_count: selectedRouteItems.length,
        total_quantity: selectedRouteItems.reduce((sum: number, item: any) => sum + Number(item.suggestedQuantity || 0), 0),
      });

      // Track funnel step
      this.eventHandler.trackFeatureStep('Inventory Transfer Suggestions', 'Transfer Creation Started', {
        items_count: selectedRouteItems.length,
      });
    }

    const firstItem = selectedRouteItems[0];

    // Calculate transfer-level priority from selected items
    const transferPriorityScore = Math.max(...selectedRouteItems.map((item) => item.priorityScore || 0));
    const transferPriorityLevel = this.getPriorityLevel(transferPriorityScore);

    // Prepare confirmation dialog data
    this.confirmTransferData = {
      fromLocationId: firstItem.fromLocationId,
      toLocationId: firstItem.toLocationId,
      fromLocationName: firstItem.fromLocationName,
      toLocationName: firstItem.toLocationName,
      priorityScore: transferPriorityScore,
      priorityLevel: transferPriorityLevel,
      note: `Transfer from ${firstItem.fromLocationName} to ${firstItem.toLocationName}`,
      items: selectedRouteItems.map((item) => ({
        // Core item data
        productId: item.productId,
        variantId: item.variantId,
        inventoryItemId: item.inventoryItemId,
        productTitle: item.productTitle,
        variantTitle: item.variantTitle,
        sku: item.sku,
        imageUrl: item.imageUrl,
        unitCost: item.unitCost,

        // Quantity data
        quantity: item.suggestedQuantity,
        maxQuantity: item.suggestedQuantity, // Store original suggested as max
        originalSuggested: item.suggestedQuantity,

        // Current inventory levels
        sourceInventory: item.sourceInventory || 0,
        destinationInventory: item.destinationInventory || 0,

        // Priority scores (keep for API but don't display)
        priorityScore: item.priorityScore || 0,
        stockoutRiskScore: item.stockoutRiskScore || 0,
        revenueImpactScore: item.revenueImpactScore || 0,
        leadTimeUrgencyScore: item.leadTimeUrgencyScore || 0,
      })),
      // Store the original suggestion for reference
      originalSuggestion: suggestion,
    };

    // Show confirmation dialog (reset to the first page of the item list)
    this.confirmDialogPage = 1;
    this.showTransferConfirmDialog = true;
  }

  public async confirmCreateTransfer(): Promise<void> {
    if (!this.confirmTransferData || !this.isTransferValid) { return; }

    // Track transfer confirmation
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Creation Confirmed', {
        from_location_id: this.confirmTransferData.fromLocationId,
        from_location_name: this.confirmTransferData.fromLocationName,
        to_location_id: this.confirmTransferData.toLocationId,
        to_location_name: this.confirmTransferData.toLocationName,
        items_count: this.confirmTransferData.items.length,
        total_quantity: this.totalQuantity,
        priority_level: this.confirmTransferData.priorityLevel,
        priority_score: this.confirmTransferData.priorityScore,
        has_note: Boolean(this.confirmTransferData.note),
      });
    }

    // Check if store has write_inventory_transfers permission to use Shopify API
    const useShopifyTransfer = this.store && this.store.hasInventoryTransferPermission && this.store.hasInventoryTransferPermission();

    const transferData: any = {
      source_location_id: this.confirmTransferData.fromLocationId,
      destination_location_id: this.confirmTransferData.toLocationId,
      note: this.confirmTransferData.note,
      priority_score: this.confirmTransferData.priorityScore,
      priority_level: this.confirmTransferData.priorityLevel,
      items: this.confirmTransferData.items.map((item: any) => ({
        // Use inventory_item_id for Shopify transfers, product_variant_id for local transfers
        ...(useShopifyTransfer ?
          { inventory_item_id: item.inventoryItemId } :
          { product_variant_id: item.variantId }
        ),
        quantity: item.quantity,
        note: `${item.productTitle} - ${item.variantTitle}`,
        // Include priority and product information from suggestions
        priority_score: item.priorityScore || 0,
        stockout_risk_score: item.stockoutRiskScore || 0,
        revenue_impact_score: item.revenueImpactScore || 0,
        lead_time_urgency_score: item.leadTimeUrgencyScore || 0,
        product_id: item.productId,
        product_title: item.productTitle,
        variant_title: item.variantTitle,
        sku: item.sku,
        image_url: item.imageUrl,
        unit_cost: item.unitCost,
        // Include both IDs for reference
        product_variant_id: item.variantId,
        inventory_item_id: item.inventoryItemId,
      })),
    };

    // Call appropriate API based on store permissions
    const result = useShopifyTransfer ?
      await this.transfersCollection.createShopifyTransfer(transferData) :
      await this.transfersCollection.createTransfer(transferData);

    if (result) {
      // Track successful transfer creation
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Creation Success', {
          transfer_id: (result as any).id,
          from_location_id: this.confirmTransferData.fromLocationId,
          to_location_id: this.confirmTransferData.toLocationId,
          items_count: this.confirmTransferData.items.length,
          total_quantity: this.totalQuantity,
        });

        // Track funnel completion
        this.eventHandler.trackFeatureCompleted('Inventory Transfer Suggestions', {
          transfer_created: true,
          items_count: this.confirmTransferData.items.length,
        });
      }

      // Clear selection for this route after successful creation
      this.clearRouteSelection(this.confirmTransferData.originalSuggestion);

      // Show success notification with transfer link
      const itemCount = this.confirmTransferData?.items?.length || 0;
      const useShopifyTransfer = this.store && this.store.hasInventoryTransferPermission && this.store.hasInventoryTransferPermission();


      if (useShopifyTransfer && (result as any).name) {
        // Build Shopify transfer URL
        const shopName = this.store.shopName;
        const transferResult = result as any;
        const transferUrl = `https://admin.shopify.com/store/${shopName.replace('.myshopify.com', '')}/transfers/${transferResult.shopify_transfer_id.split('/').pop()}`;

        // Show success notification with link
        this.showTransferSuccessNotification(itemCount, transferUrl, transferResult.name);
      } else {
        // Show regular success notification for local transfers
        const message = `Successfully created transfer request with ${itemCount} item${itemCount !== 1 ? 's' : ''}!`;
        EventBus.$emit('show-snackbar', {
          message,
          color: 'success',
          icon: 'mdi-check-circle',
        });
      }

      // Close the confirmation dialog
      this.showTransferConfirmDialog = false;
      this.confirmTransferData = null;

      // Reload suggestions data to reflect pending transfers — full reset so
      // summaries (counts/pending) are re-tallied via need_count
      this.reloadingAfterTransfer = true;
      try {
        await this.initSuggestionPanels();
      } catch (error) {
        EventBus.$emit('show-snackbar', {
          message: 'Transfer created but failed to refresh data. Please reload the page.',
          color: 'warning',
          icon: 'mdi-alert',
        });
      } finally {
        this.reloadingAfterTransfer = false;
      }
    } else {
      // Track failed transfer creation
      if (this.eventHandler) {
        this.eventHandler.track('Inventory Transfer Creation Failed', {
          from_location_id: this.confirmTransferData.fromLocationId,
          to_location_id: this.confirmTransferData.toLocationId,
          items_count: this.confirmTransferData.items.length,
          total_quantity: this.totalQuantity,
          error_type: 'api_error',
        });

        // Track funnel abandonment
        this.eventHandler.trackFeatureAbandoned('Inventory Transfer Suggestions', 'transfer_creation_failed', {
          items_count: this.confirmTransferData.items.length,
        });
      }

      // Show error notification — surface the backend's message (e.g. the
      // item-count cap or a partial Shopify creation) instead of a generic one
      EventBus.$emit('show-snackbar', {
        message: this.transfersCollection.error || 'Failed to create transfer request. Please try again.',
        color: 'error',
        icon: 'mdi-alert-circle',
      });
    }
  }

  public cancelTransferConfirm(): void {
    // Track transfer cancellation
    if (this.eventHandler && this.confirmTransferData) {
      this.eventHandler.track('Inventory Transfer Creation Cancelled', {
        from_location_id: this.confirmTransferData.fromLocationId,
        to_location_id: this.confirmTransferData.toLocationId,
        items_count: this.confirmTransferData.items.length,
        total_quantity: this.totalQuantity,
        stage: 'confirmation_dialog',
      });

      // Track funnel abandonment
      this.eventHandler.trackFeatureAbandoned('Inventory Transfer Suggestions', 'user_cancelled', {
        at_stage: 'confirmation_dialog',
      });
    }

    this.showTransferConfirmDialog = false;
    this.confirmTransferData = null;
  }

  public updateQuantity(index: number, newQuantity: number): void {
    if (this.confirmTransferData && this.confirmTransferData.items[index]) {
      // Allow quantities up to source inventory, not just suggested quantity
      const maxAllowed = this.confirmTransferData.items[index].sourceInventory;
      this.confirmTransferData.items[index].quantity = Math.max(0, Math.min(newQuantity, maxAllowed));
    }
  }

  public removeItemFromTransfer(index: number): void {
    if (this.confirmTransferData && this.confirmTransferData.items) {
      this.confirmTransferData.items.splice(index, 1);
      // Keep the current page in range if the last item on the last page was removed
      const pageCount = this.confirmDialogPageCount;
      if (pageCount > 0 && this.confirmDialogPage > pageCount) {
        this.confirmDialogPage = pageCount;
      }
    }
  }

  public showTransferSuccessNotification(itemCount: number, transferUrl: string, transferName: string): void {
    // Check if we're in embedded mode using Shopify's proper detection
    const isEmbeddedMode = isEmbedded();

    // Store transfer data for the dialog
    this.lastCreatedTransfer = {
      name: transferName,
      url: transferUrl,
      isEmbedded: isEmbeddedMode,
      itemCount,
    };

    // Show success dialog
    this.showTransferSuccessDialog = true;
  }

  public closeSuccessDialog(): void {
    this.showTransferSuccessDialog = false;
    this.lastCreatedTransfer = null;
  }

  public openTransferInShopify(): void {
    if (this.lastCreatedTransfer) {
      this.openShopifyTransfer(this.lastCreatedTransfer.url, this.lastCreatedTransfer.isEmbedded);
      this.closeSuccessDialog();
    }
  }

  public openShopifyTransfer(transferUrl: string, isEmbeddedMode: boolean): void {
    // Track transfer link click
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Shopify Link Clicked', {
        transfer_url: transferUrl,
        is_embedded: isEmbeddedMode,
        redirect_method: 'new_tab'
      });
    }

    // Open transfer in new tab for both embedded and non-embedded modes
    window.open(transferUrl, '_blank');
  }

  // Story 15: "all selected" is judged against the server-side total of items
  // matching the current filters, since only one page of items is loaded.
  public isAllSelectedForRoute(suggestion: any): boolean {
    const state = this.getSuggestionState(suggestion);
    if (state.total === 0) { return false; }
    return this.getSelectedItemsForRoute(suggestion).length >= state.total;
  }

  public isPartialSelectedForRoute(suggestion: any): boolean {
    const state = this.getSuggestionState(suggestion);
    if (state.total === 0) { return false; }
    const selectedCount = this.getSelectedItemsForRoute(suggestion).length;
    return selectedCount > 0 && selectedCount < state.total;
  }

  // Story 15: select-all targets every item matching the current search/filters
  // across ALL pages — fetched as slim records via the ids_only selection call.
  public async toggleSelectAllForRoute(suggestion: any): Promise<void> {
    if (this.isAllSelectedForRoute(suggestion)) {
      this.clearRouteSelection(suggestion);
      return;
    }
    // Nothing matches when every priority chip is deselected — see
    // fetchSuggestionItemsForRoute for why this must not reach the server
    if (this.priorityFilters.priorityLevels.length === 0) {
      return;
    }

    const state = this.getSuggestionState(suggestion);
    state.loading = true;
    try {
      const selection = await InventoryTransferSuggestionItems.fetchSelection({
        fromLocationId: suggestion.fromLocationId,
        toLocationId: suggestion.toLocationId,
        ...this.currentItemFilterParams(),
      });
      selection.items.forEach((item: any) => {
        if (item.suggestedQuantity === 0) { return; }
        const itemKey = `${item.variantId}-${suggestion.fromLocationId}-${suggestion.toLocationId}`;
        if (!this.selectedItems[itemKey]) {
          this.$set(this.selectedItems, itemKey, {
            ...item,
            fromLocationId: suggestion.fromLocationId,
            toLocationId: suggestion.toLocationId,
            fromLocationName: this.getLocationName(suggestion.fromLocationId),
            toLocationName: this.getLocationName(suggestion.toLocationId),
          });
        }
      });
    } catch (e) {
      console.error('Failed to select all suggestions for route:', e);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to select all items. Please try again.',
        color: 'error',
        icon: 'mdi-alert-circle',
      });
    } finally {
      state.loading = false;
    }
  }

  public getLocationName(locationId: number): string {
    return this.locationMap.get(locationId) || `Location ${locationId}`;
  }

  public getRouteForSuggestion(suggestion: any): InventoryTransferRouteModel | null {
    return this.routes.items.find((route) => {
      const r = route as InventoryTransferRouteModel;
      return Number(r.sourceLocationId) === Number(suggestion.fromLocationId) &&
             Number(r.destinationLocationId) === Number(suggestion.toLocationId);
    }) as InventoryTransferRouteModel || null;
  }

  public openExplanationDialog(item: any, parentSuggestion?: any): void {
    this.selectedItem = item;
    // Story 15: the route is passed explicitly from the panel context (item
    // arrays on the route models are no longer populated, so lookup by
    // membership is not possible)
    if (parentSuggestion) {
      this.selectedRoute = this.getRouteForSuggestion(parentSuggestion);
    }

    // Track explanation dialog opened
    if (this.eventHandler && item) {
      this.eventHandler.track('Inventory Transfer Explanation Viewed', {
        product_id: item.productId,
        variant_id: item.variantId,
        product_title: item.productTitle,
        variant_title: item.variantTitle,
        sku: item.sku,
        suggested_quantity: item.suggestedQuantity,
        priority_level: this.getPriorityLevel(item.priorityScore || 0),
        priority_score: item.priorityScore || 0,
        from_location_id: parentSuggestion?.fromLocationId,
        from_location_name: parentSuggestion ? this.getLocationName(parentSuggestion.fromLocationId) : null,
        to_location_id: parentSuggestion?.toLocationId,
        to_location_name: parentSuggestion ? this.getLocationName(parentSuggestion.toLocationId) : null,
        has_pending_transfer: item.hasPendingTransfer || false,
        pending_quantity: item.pendingTransferQuantity || 0,
      });
    }

    this.showExplanationDialog = true;
  }

  // Dialog calculation methods using real API data
  // Story 15: the route is captured in selectedRoute when the dialog opens
  // (membership lookup over item arrays is no longer possible)
  public getSourceLocationName(): string {
    return this.selectedRoute
      ? this.getLocationName(Number(this.selectedRoute.sourceLocationId))
      : 'Unknown';
  }

  public getDestinationLocationName(): string {
    return this.selectedRoute
      ? this.getLocationName(Number(this.selectedRoute.destinationLocationId))
      : 'Unknown';
  }

  public getSelectedStrategy(): string {
    if (!this.selectedItem) { return 'Unknown'; }
    const strategy = this.selectedItem.destinationStrategy;
    if (strategy === 'FORECAST') { return 'Forecast-Based'; }
    if (strategy === 'MIN_MAX') { return 'Min/Max-Based'; }
    if (strategy === 'FIXED_QUANTITY') { return 'Fixed Quantity'; }
    return strategy || 'Forecast-Based'; // Default fallback
  }

  public getStrategyDescription(): string {
    const strategy = this.getSelectedStrategy();
    if (strategy === 'Forecast-Based') {
      return 'Uses sales forecasts to dynamically calculate reorder points and target inventory levels';
    } else if (strategy === 'Min/Max-Based') {
      return 'Uses forecasted sales with defined minimum and maximum stock day ranges';
    } else if (strategy === 'Fixed Quantity') {
      return 'Uses absolute minimum and maximum inventory quantities';
    }
    return 'Strategy-based inventory management';
  }

  // --- Step 3: source availability constraint ---
  //
  // The backend never caps a transfer at the source's raw on-hand. It caps at
  // `source_transferable_quantity` — on-hand MINUS whatever the source's own
  // replenishment strategy reserves for itself (see
  // olap_inventory_transfer_recommendations.yml: `from_settings.max_quantity`
  // for FIXED_QUANTITY, avg-daily-sales × max_stock_days for MIN_MAX, ×
  // (safety + target) stock days for FORECAST). Reading the cap off
  // `sourceInventory` overstates it by exactly the reserved amount, which is
  // most glaring for a FIXED_QUANTITY source where that number is hand-set.
  public getSourceAvailable(): number {
    if (!this.selectedItem) { return 0; }
    return Math.round((this.selectedItem.transferableQuantity || 0) * 100) / 100;
  }

  public getSourceOnHand(): number {
    if (!this.selectedItem) { return 0; }
    return Math.round((this.selectedItem.sourceInventory || 0) * 100) / 100;
  }

  public getSourceRetained(): number {
    if (!this.selectedItem) { return 0; }
    const retained = (this.selectedItem.sourceInventory || 0) - (this.selectedItem.transferableQuantity || 0);
    return Math.round(Math.max(0, retained) * 100) / 100;
  }

  public getTransferNeed(): number {
    if (!this.selectedItem) { return 0; }
    const need = (this.selectedItem.destinationNeed || 0) - (this.selectedItem.pendingTransferQuantity || 0);
    return Math.round(need * 100) / 100;
  }

  public getSourceStrategy(): string {
    if (!this.selectedItem) { return 'Forecast-Based'; }
    const strategy = this.selectedItem.sourceStrategy;
    if (strategy === 'FORECAST') { return 'Forecast-Based'; }
    if (strategy === 'MIN_MAX') { return 'Min/Max-Based'; }
    if (strategy === 'FIXED_QUANTITY') { return 'Fixed Quantity'; }
    return strategy || 'Forecast-Based';
  }

  // The setting a merchant would lower to free up more of the source's stock.
  public getSourceRetentionSetting(): string {
    const strategy = this.getSourceStrategy();
    if (strategy === 'Fixed Quantity') { return 'max quantity'; }
    if (strategy === 'Min/Max-Based') { return 'max stock days'; }
    return 'safety stock days and target stock days';
  }

  public isSourceLimited(): boolean {
    return this.getSourceAvailable() < this.getTransferNeed();
  }

  public getSourceRetentionClause(): string {
    const retained = this.getSourceRetained();
    if (retained <= 0) { return ''; }
    return ` its ${this.getSourceStrategy()} strategy reserves ${retained} units ` +
      `to hold the source at its ${this.getSourceRetentionSetting()}.`;
  }

  public getStep3Explanation(): string {
    if (!this.selectedItem) { return ''; }
    const available = this.getSourceAvailable();
    const onHand = this.getSourceOnHand();
    const need = this.getTransferNeed();
    const clause = this.getSourceRetentionClause();
    if (this.isSourceLimited()) {
      return `The transfer is limited by source availability. The transfer need is ${need} units, ` +
        `but only ${available} of its ${onHand} units on hand can be transferred —` +
        (clause || ` the source has ${available} units to spare.`);
    }
    if (this.getSourceRetained() > 0) {
      return `${available} of its ${onHand} units on hand can be transferred — ` +
        `enough to cover the transfer need of ${need} units. Of the remainder,` + clause;
    }
    return `The source can transfer all ${onHand} units on hand. ` +
      `The full transfer need of ${need} units can be fulfilled.`;
  }

  public getCalculatedFDS(): number {
    if (!this.selectedItem) { return 0; }
    // Use actual destination average daily sales from API
    return Math.round((this.selectedItem.destinationAvgDailySales || 0) * 100) / 100;
  }

  public getSafetyStockDays(): number {
    if (!this.selectedItem) { return 14; }
    return this.selectedItem.destinationSafetyStockDays || 14;
  }

  public getTargetStockDays(): number {
    if (!this.selectedItem) { return 30; }
    if (this.getSelectedStrategy() === 'Min/Max-Based') {
      return this.selectedItem.destinationMaxStockDays || 30;
    }
    return this.selectedItem.destinationTargetStockDays || 30;
  }

  public getCalculatedSafetyStock(): number {
    return Math.round(this.getCalculatedFDS() * this.getSafetyStockDays() * 100) / 100;
  }

  public getCalculatedDDLT(): number {
    if (!this.selectedRoute) { return 0; }
    return Math.round(this.getCalculatedFDS() * this.selectedRoute.leadTimeDays * 100) / 100;
  }

  public getCalculatedTIL(): number {
    if (!this.selectedItem) { return 0; }
    // For FIXED_QUANTITY strategy, use max_quantity directly from API
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return Math.round((this.selectedItem.destinationMaxQuantity || 0) * 100) / 100;
    }
    // For other strategies, calculate based on FDS
    return Math.ceil(this.getCalculatedFDS() * this.getTargetStockDays());
  }

  public getCalculatedROP(): number {
    if (!this.selectedItem) { return 0; }
    // For FIXED_QUANTITY strategy, use min_quantity directly from API
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return Math.round((this.selectedItem.destinationMinQuantity || 0) * 100) / 100;
    }
    // For other strategies, use reorder point from API
    return Math.round((this.selectedItem.reorderPoint || 0) * 100) / 100;
  }

  public getSafetyStockFormula(): string {
    return `FDS × Safety Stock Days = ${this.getCalculatedFDS()} × ${this.getSafetyStockDays()}`;
  }

  public getDDLTFormula(): string {
    if (!this.selectedRoute) { return ''; }
    return `FDS × Lead Time Days = ${this.getCalculatedFDS()} × ${this.selectedRoute.leadTimeDays}`;
  }

  public getTILFormula(): string {
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return `Fixed Maximum Quantity = ${this.getCalculatedTIL()}`;
    }
    const daysLabel = this.getSelectedStrategy() === 'Forecast-Based' ? 'Target Stock Days' : 'Max Stock Days';
    return `FDS × ${daysLabel} = ${this.getCalculatedFDS()} × ${this.getTargetStockDays()}`;
  }

  public getROPFormula(): string {
    if (this.getSelectedStrategy() === 'Fixed Quantity') {
      return `Fixed Minimum Quantity = ${this.getCalculatedROP()}`;
    }
    if (this.getSelectedStrategy() === 'Forecast-Based') {
      return `DDLT + SS = ${this.getCalculatedDDLT()} + ${this.getCalculatedSafetyStock()}`;
    } else {
      const minDays = this.selectedItem?.destinationMinStockDays || this.selectedItem?.minStockDays || 7;
      return `FDS × Min Stock Days = ${this.getCalculatedFDS()} × ${minDays}`;
    }
  }

  public isTriggered(): boolean {
    if (!this.selectedItem) { return false; }
    return this.selectedItem.destinationInventory <= this.selectedItem.reorderPoint;
  }

  // Story 15: true while a route's summary (need_count) is still in flight.
  // Falls back to false (zeros show) if the fetch errored, so the spinner
  // can't run forever.
  public isRouteSummaryLoading(route: InventoryTransferRouteModel): boolean {
    const key = `${Number(route.sourceLocationId)}_${Number(route.destinationLocationId)}`;
    const state = this.suggestionItemsState[key];
    if (!state) { return true; } // fetch not started yet
    return !state.summaryLoaded && (state.loading || !state.loaded);
  }

  public getRouteSummary(route: InventoryTransferRouteModel) {
    // Story 15: totals come from the per-route summary fetched with the route's
    // first /suggestion-items call (need_count)
    const key = `${Number(route.sourceLocationId)}_${Number(route.destinationLocationId)}`;
    const summary = this.suggestionItemsState[key]?.summary;
    return {
      variantCount: summary?.countVariants || 0,
      totalQuantity: Math.round((summary?.totalSuggestedQuantity || 0) * 100) / 100,
    };
  }

  public clearLocationSelection(): void {
    this.selectedLocationId = null;
  }

  public onLocationSelected(locationId: number): void {

    // Track location selection
    if (this.eventHandler && locationId) {
      const locationName = this.getLocationName(locationId);
      this.eventHandler.track('Inventory Transfer Location Selected', {
        location_id: locationId,
        location_name: locationName,
        selection_mode: this.selectionMode,
        available_routes: this.filteredRoutes.length,
        total_suggestions: this.filteredSuggestions.length,
      });

      // Track funnel step
      this.eventHandler.trackFeatureStep('Inventory Transfer Suggestions', 'Location Selected', {
        location_id: locationId,
        mode: this.selectionMode,
      });
    }
  }

  public redirectToSettings(): void {
    this.$router.push('/settings/internal-inventory-transfer-settings');
  }

  public trackHelpGuideClick(): void {
    // Track help guide click
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Help Guide Clicked', {
        page: 'inventory_transfer_suggestions',
        help_url: 'https://help.assisty.ai/en/article/inventory-transfer-suggestions-user-guide-rodpb4/',
        user_context: {
          has_selected_location: Boolean(this.selectedLocationId),
          selection_mode: this.selectionMode,
          total_routes: this.routes.items.length,
          total_suggestions: this.filteredSuggestions.length
        }
      });
    }
  }

  public setSelectionMode(mode: 'source' | 'destination'): void {

    // Track mode selection
    if (this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Mode Selected', {
        mode,
        previous_mode: this.selectionMode,
        available_locations: this.availableLocations.length,
      });
    }

    this.selectionMode = mode;
    // Clear selected location when changing mode since available locations might change
    this.selectedLocationId = null;
  }

  public customFilter(item: any, queryText: string, itemText: string): boolean {
    const searchText = queryText.toLowerCase();
    const locationName = (item.name || '').toLowerCase();
    const locationId = (item.id || '').toString().toLowerCase();

    return locationName.includes(searchText) || locationId.includes(searchText);
  }

  public scrollToSuggestions(route: InventoryTransferRouteModel): void {
    const key = `${Number(route.sourceLocationId)}_${Number(route.destinationLocationId)}`;
    const state = this.suggestionItemsState[key];
    // Scroll when the route has (or may still have) suggestions
    if (state && (!state.summaryLoaded || (state.summary?.countVariants || 0) > 0)) {
      const suggestionsSection = document.querySelector('.suggestions-section');
      if (suggestionsSection) {
        suggestionsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }

  public scrollToRouteInfo(route: InventoryTransferRouteModel): void {
    const routesContainer = document.querySelector('.routes-container');
    if (routesContainer) {
      routesContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  public async fetchData(): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      // Story 15: no summary endpoint call — panels come from the configured
      // routes (cheap), and each route's items + summary load together via
      // /suggestion-items?need_count=true
      await Promise.all([
        this.locations.fetch({limit: 250}),
        this.routes.fetch(),
      ]);
      this.initSuggestionPanels();
    } catch (e: any) {
      this.error = 'Failed to load data: ' + e.message;
    } finally {
      this.loading = false;
    }
  }

  // Reset exclusion panel state when the user switches location (B5)
  @Watch('selectedLocationId')
  public onSelectedLocationChange(): void {
    this.openExclusionPanels = [];
    this.excludedItemsState = {};
    this.selectedExclusionReasons = [];
    this.excludedSearchQuery = '';
    // Story 14: reset the suggestions search so a stale query doesn't hide a new location's items
    this.suggestionSearchQuery = '';
    this.appliedSuggestionSearch = '';
    // Reset product filters BEFORE clearing the FilterColumn UI: the emitted
    // input([]) then finds state already empty and skips its refetch —
    // initSuggestionPanels below does the (single) reload.
    this.productFilterColumns = new FilterColumnList();
    EventBus.$emit('clear-filter-column');
    // Story 15: the route panels changed — reset per-route item state and
    // open/load the first panel of the new location
    this.initSuggestionPanels();
  }

  // Lifecycle methods

  public beforeDestroy(): void {
    // Clean up debounce timers to prevent calls on a destroyed component (B3)
    if (this.excludedSearchDebounceTimer !== null) {
      window.clearTimeout(this.excludedSearchDebounceTimer);
      this.excludedSearchDebounceTimer = null;
    }
    if (this.filterRefetchTimer !== null) {
      window.clearTimeout(this.filterRefetchTimer);
      this.filterRefetchTimer = null;
    }
  }

  public async mounted(): Promise<void> {
    // Track page view
    if (this.store) {
      this.eventHandler.track('Inventory Transfer Suggestions Page Viewed', {
        page: 'inventory_transfer_suggestions',
        has_locations: false,
        has_routes: false,
        total_locations: 0,
        total_routes: 0,
      });

      // Track funnel step
      this.eventHandler.trackFeatureStep('Inventory Transfer Suggestions', 'Page Viewed');
    }

    await this.fetchData();

    // Track data loading completion with updated metrics
    if (this.store && this.eventHandler) {
      this.eventHandler.track('Inventory Transfer Suggestions Data Loaded', {
        total_locations: this.locations.items.length,
        total_routes: this.routes.items.length,
        has_locations: this.locations.items.length > 0,
        has_routes: this.routes.items.length > 0,
        available_locations: this.availableLocations.length,
      });
    }
  }

  // ============================================================================
  // CSV EXPORT METHODS
  // ============================================================================

  /**
   * Export transfer suggestions to CSV file
   */
  public async exportToCSV(): Promise<void> {
    try {
      // Get data to export (all filtered or selected only)
      const dataToExport = await this.getExportData();

      if (dataToExport.length === 0) {
        EventBus.$emit('show-snackbar', {
          message: 'No suggestions to export',
          color: 'warning',
          icon: 'mdi-alert',
        });
        return;
      }

      // Sort data based on view mode
      const sortedData = this.sortForExport(dataToExport);

      // Map to CSV format
      const csvRows = sortedData.map((item) => this.mapSuggestionToCSV(item));

      // Generate filename
      const filename = this.generateExportFilename();

      // Create CSV data structure
      const csvData = {
        headers: Object.keys(csvRows[0]),
        rows: csvRows.map((row) => Object.values(row)),
      };

      // Download CSV
      CSVExportUtil.downloadCSVData(csvData, filename);

      // Show success message
      EventBus.$emit('show-snackbar', {
        message: `Exported ${csvRows.length} suggestion${csvRows.length !== 1 ? 's' : ''} to CSV`,
        color: 'success',
        icon: 'mdi-download',
      });

      // Track export event
      if (this.eventHandler) {
        this.eventHandler.track('Transfer Suggestions Exported', {
          total_exported: csvRows.length,
          selection_mode: this.selectionMode,
          location_id: this.selectedLocationId,
          export_scope: this.hasSelectedItems ? 'selected' : 'all',
        });
      }
    } catch (error) {
      EventBus.$emit('show-snackbar', {
        message: 'Failed to export CSV. Please try again.',
        color: 'error',
        icon: 'mdi-alert-circle',
      });
    }
  }

  /**
   * Get data to export - selected items or all filtered suggestions
   */
  private async getExportData(): Promise<any[]> {
    // If items are selected, export those — selections carry their full data,
    // so no fetch is needed. Scope to the currently shown location's routes.
    if (this.hasSelectedItems) {
      const routeKeys = new Set(this.filteredSuggestions.map(
        (s: any) => `${s.fromLocationId}-${s.toLocationId}`,
      ));
      return Object.values(this.selectedItems).filter((item: any) =>
        routeKeys.has(`${item.fromLocationId}-${item.toLocationId}`),
      );
    }

    // Story 15: items are no longer held client-side. Export everything that
    // matches the CURRENT search/priority/vendor/price filters by reusing the
    // select-all contract per route (slim rows, no pagination) — so the CSV
    // always mirrors what the filtered tables show.
    if (this.priorityFilters.priorityLevels.length === 0) {
      return []; // every priority chip deselected = nothing matches
    }
    const perRoute = await Promise.all(this.filteredRoutes.map(async (r: any) => {
      const fromLocationId = Number(r.sourceLocationId);
      const toLocationId = Number(r.destinationLocationId);
      const selection = await InventoryTransferSuggestionItems.fetchSelection({
        fromLocationId,
        toLocationId,
        ...this.currentItemFilterParams(),
      });
      // Slim rows lack route context — inject it for the CSV columns/sort
      return selection.items.map((item: any) => ({
        ...item,
        sourceLocationId: fromLocationId,
        destinationLocationId: toLocationId,
        leadTimeDays: r.leadTimeDays || 0,
      }));
    }));
    return perRoute.flat();
  }

  /**
   * Sort export data based on view mode
   * Uses getLocationName() to get location names by ID
   */
  private sortForExport(data: any[]): any[] {
    return [...data].sort((a, b) => {
      // Primary sort: by location based on view mode
      let primaryCompare = 0;

      if (this.selectionMode === 'destination') {
        // Destination mode: sort by source location (from)
        const aLocation = this.getLocationName(a.sourceLocationId);
        const bLocation = this.getLocationName(b.sourceLocationId);
        primaryCompare = aLocation.localeCompare(bLocation);
      } else {
        // Source mode: sort by destination location (to)
        const aLocation = this.getLocationName(a.destinationLocationId);
        const bLocation = this.getLocationName(b.destinationLocationId);
        primaryCompare = aLocation.localeCompare(bLocation);
      }

      // If locations are same, sort by priority score (descending)
      if (primaryCompare === 0) {
        return (b.priorityScore || 0) - (a.priorityScore || 0);
      }

      return primaryCompare;
    });
  }

  /**
   * Map suggestion item to CSV row format
   * Uses getLocationName() to lookup location names by ID for accuracy
   */
  private mapSuggestionToCSV(item: any): Record<string, any> {
    return {
      'Product Title': item.productTitle || '',
      'Variant Title': item.variantTitle || '',
      'SKU': item.sku || '',
      'Vendor': item.vendor || '',
      'Barcode': item.barcode || '',
      'Unit Price': item.unitPrice || 0,
      'Total Price': item.totalPrice || 0,
      'Priority Level': this.getPriorityLevel(item.priorityScore || 0),
      'Priority Score': item.priorityScore || 0,
      'Source Location': this.getLocationName(item.sourceLocationId),
      'Source Inventory': item.sourceInventory || 0,
      'Destination Location': this.getLocationName(item.destinationLocationId),
      'Destination Inventory': item.destinationInventory || 0,
      'Suggested Quantity': item.suggestedQuantity || 0,
      'Lead Time (Days)': item.leadTimeDays || 0,
      'Has Pending Transfer': item.hasPendingTransfer ? 'Yes' : 'No',
    };
  }

  /**
   * Generate export filename with metadata
   */
  private generateExportFilename(): string {
    const mode = this.selectionMode === 'destination' ? 'to' : 'from';
    const locationName = this.selectedLocationId !== null
      ? this.getLocationName(this.selectedLocationId) || 'all'
      : 'all';
    const location = this.sanitizeFilename(locationName);
    const timestamp = this.formatDate(new Date(), 'YYYY-MM-DD');

    return `transfer_suggestions_${mode}_${location}_${timestamp}.csv`;
  }

  /**
   * Sanitize filename string
   */
  private sanitizeFilename(name: string): string {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  /**
   * Format date for filename or display
   */
  private formatDate(date: Date, format: string): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    if (format === 'YYYY-MM-DD') {
      return `${year}-${month}-${day}`;
    }

    return `${year}-${month}-${day}`;
  }
}
